import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MailOpen } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/components/toast";
import { Button } from "@/components/ui";
import { AuthShell } from "@/components/auth-shell";

export default function Verify() {
  const navigate = useNavigate();
  const toast = useToast();
  const { signIn } = useAuth();
  const [params] = useSearchParams();
  const email = params.get("email") ?? "";
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const inputRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const verify = async (e) => {
    e?.preventDefault();
    if (code.length !== 6) return toast("Enter the 6-digit code", "error");
    setLoading(true);
    try {
      const res = await api("/auth/verify-email", { method: "POST", auth: false, body: { email, code } });
      await signIn(res.token, res.user);
      toast("Email verified. Welcome to LOVE NET!", "success");
      navigate("/onboarding", { replace: true });
    } catch (err) {
      toast(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api("/auth/resend-code", { method: "POST", auth: false, body: { email } });
      toast("A new code is on its way", "success");
      setCooldown(60);
    } catch (err) {
      toast(err.message, "error");
    }
  };

  return (
    <AuthShell backTestId="verify-back-button" onBack={() => navigate("/login", { replace: true })}>
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#F6E3E4]"><MailOpen className="h-8 w-8 text-primary" /></div>
      <h1 className="text-[34px] leading-tight">Confirm your email</h1>
      <p className="mt-2 text-[15px] leading-6 text-muted-foreground">
        We sent a 6-digit code to <span className="font-bold text-foreground" data-testid="verify-email">{email}</span>. Your account stays inactive until it is verified.
      </p>
      <form onSubmit={verify}>
        <label htmlFor="otp" className="sr-only">Verification code</label>
        <div className="relative mt-8" onClick={() => inputRef.current?.focus()} data-testid="verify-code-boxes">
          <div className="flex justify-between gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={`flex h-[58px] w-12 items-center justify-center rounded-xl border bg-white font-display text-[28px] font-bold text-primary sm:w-14 ${code.length === i ? "border-[1.5px] border-primary" : "border-border"}`}>
                {code[i] ?? ""}
              </div>
            ))}
          </div>
          <input id="otp" ref={inputRef} data-testid="verify-code-input" value={code} autoFocus inputMode="numeric" autoComplete="one-time-code" maxLength={6}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} className="absolute inset-0 h-full w-full cursor-text opacity-0" />
        </div>
        <Button type="submit" testId="verify-submit-button" title="Verify & continue" loading={loading} disabled={code.length !== 6} className="mt-8 w-full" />
      </form>
      <button data-testid="verify-resend-button" disabled={cooldown > 0} onClick={resend} className={`mt-2 w-full py-4 text-sm font-bold ${cooldown > 0 ? "text-muted-foreground" : "text-primary"}`}>
        {cooldown > 0 ? `Resend code in ${cooldown}s` : "Resend code"}
      </button>
    </AuthShell>
  );
}
